import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import localStorageConstants from "src/app/constants/localStorage.constants";
import type { WeeklyTemplate } from "src/app/models/screens/availabilityTimeslot.model";
import TimeSlot from "src/app/models/screens/availabilityTimeslot.model";
import {
  getParentChildren,
  getUserTags,
  getWeeklyTemplate,
} from "src/app/utils/backendEndpointCalls.util";
import { minutesToTimeString } from "src/app/utils/time.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

const AvaialbilityManagementScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(true);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [role, setRole] = useState<string>("");
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedAvailabilityOwnerId, setSelectedAvailabilityOwnerId] =
    useState<string>("you");
  const [tags, setTags] = useState<Map<string, string>>(new Map());
  const [userWeeklyTemplate, setUserWeeklyTemplate] = useState<WeeklyTemplate>({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  });
  const [isWeeklyTemplateExpanded, setIsWeeklyTemplateExpanded] = useState<
    Record<string, boolean>
  >({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false,
  });
  const navigate = useNavigate();

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);
  }, []);

  const getParentChildrenAsync = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setChildren(response.children ?? []);
  }, []);

  const getWeeklyTemplateAsync = useCallback(async (childId?: string) => {
    const response = await getWeeklyTemplate(childId);

    if (!response.success) {
      showError(response.error);
      return;
    }
    setUserWeeklyTemplate(response.weeklyTemplate);
  }, []);

  const getUserTagsAsync = useCallback(async (childId?: string) => {
    const getTagsResponse = await getUserTags(childId);
    if (!getTagsResponse.success) {
      showError(getTagsResponse.error);
      return;
    }
    const nextTags = new Map<string, string>();
    Object.keys(getTagsResponse.tags ?? {}).forEach((key: string) => {
      nextTags.set(key, getTagsResponse.tags[key]);
    });
    setTags(nextTags);
  }, []);

  useEffect(() => {
    if (role !== "Parent") return;
    getParentChildrenAsync();
  }, [role, getParentChildrenAsync]);

  useEffect(() => {
    getWeeklyTemplateAsync(
      selectedAvailabilityOwnerId === "you"
        ? undefined
        : selectedAvailabilityOwnerId,
    );
    getUserTagsAsync(
      selectedAvailabilityOwnerId === "you"
        ? undefined
        : selectedAvailabilityOwnerId,
    );
  }, [selectedAvailabilityOwnerId, getUserTagsAsync, getWeeklyTemplateAsync]);

  useEffect(() => {
    let shouldUpdate: boolean = false;
    const sortedTemplate: WeeklyTemplate = { ...userWeeklyTemplate };
    (Object.keys(userWeeklyTemplate) as (keyof WeeklyTemplate)[]).forEach(
      (day: string) => {
        const timeslots: TimeSlot[] = userWeeklyTemplate[day];
        if (timeslots.length <= 1) return;
        const isSorted: boolean = timeslots.every(
          (slot, index) =>
            index === 0 || timeslots[index - 1].from <= slot.from,
        );
        if (!isSorted) {
          shouldUpdate = true;
          sortedTemplate[day] = [...timeslots].sort((a, b) => a.from - b.from);
        }
      },
    );
    if (shouldUpdate) setUserWeeklyTemplate(sortedTemplate);
  }, [userWeeklyTemplate]);

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setIsDisplayingSnackbar(true);
    setSnackbarMessage(message);
  };

  const onSelectedAvailabilityOwnerChanged = (ownerId: string) => {
    setSelectedAvailabilityOwnerId(ownerId);
    setIsWeeklyTemplateExpanded({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
    });
  };

  const onWeeklyTemplateDayExpansionUpdated = (
    dayOfWeek: string,
    expanded: boolean,
  ) => {
    setIsWeeklyTemplateExpanded({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
      [dayOfWeek]: expanded,
    });
  };

  const onCalendarClicked = () => {
    navigate("/availabilityCalendar");
  };

  return {
    days,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userWeeklyTemplate,
    onWeeklyTemplateDayExpansionUpdated,
    isWeeklyTemplateExpanded,
    minutesToTimeString,
    onCalendarClicked,
    tags,
    role,
    children,
    isLoadingChildren,
    selectedAvailabilityOwnerId,
    onSelectedAvailabilityOwnerChanged,
  };
};

export default AvaialbilityManagementScreenViewModel;
